import React, { useState, useEffect, useRef } from 'react';
import { getForms } from '../api/forms';
import './FormWheel.css';

// 直接使用环境变量
const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export default function FormWheel() {
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(true);
  const wheelRef = useRef(null);
  const [error, setError] = useState('');
  const [cardSpinning, setCardSpinning] = useState({}); // 记录每个卡片的转盘状态

  // 加载表单列表
  useEffect(() => {
    loadForms();
  }, []);

  const loadForms = async () => {
    try {
      setLoading(true);
      const response = await getForms();
      if (response.code === 200) {
        setForms(response.data || []);
      } else {
        setError('加载表单列表失败');
      }
    } catch (error) {
      console.error('加载表单失败:', error);
      setError('加载表单列表失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 计算转盘项的角度
  const getItemAngle = (index, total) => {
    return (360 / total) * index;
  };

  // 开始转盘
  const startSpin = () => {
    if (forms.length === 0 || isSpinning) return;
    
    setIsSpinning(true);
    setSelectedForm(null);
    
    // 随机选择一个表单
    const randomIndex = Math.floor(Math.random() * forms.length);
    const targetForm = forms[randomIndex];
    
    // 计算目标角度
    const targetAngle = 360 * 5 + getItemAngle(randomIndex, forms.length); // 转5圈加上目标角度
    const currentRotation = rotation % 360;
    const finalRotation = currentRotation + targetAngle;
    
    setRotation(finalRotation);
    
    // 设置转盘动画
    if (wheelRef.current) {
      wheelRef.current.style.transition = 'transform 3s cubic-bezier(0.17, 0.67, 0.12, 0.99)';
      wheelRef.current.style.transform = `rotate(${finalRotation}deg)`;
    }
    
    // 3秒后停止并显示结果
    setTimeout(() => {
      setIsSpinning(false);
      setSelectedForm(targetForm);
      
      // 重置过渡效果
      if (wheelRef.current) {
        wheelRef.current.style.transition = 'none';
      }
    }, 3000);
  };

  // 打开表单
  const openForm = (form) => {
    window.open(`/detail/${form.id}`, '_blank');
  };

  // 单个表单卡片转盘
  const spinCardWheel = (formId) => {
    if (cardSpinning[formId]) return;
    
    setCardSpinning(prev => ({ ...prev, [formId]: true }));
    
    // 模拟转盘效果，随机决定是否直接打开表单
    setTimeout(() => {
      const shouldOpen = Math.random() > 0.3; // 70%概率直接打开
      
      if (shouldOpen) {
        const form = forms.find(f => f.id === formId);
        if (form) {
          openForm(form);
        }
      }
      
      setCardSpinning(prev => ({ ...prev, [formId]: false }));
    }, 1500);
  };

  if (loading) {
    return (
      <div className="form-wheel-container">
        <div className="loading">正在加载表单列表...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-wheel-container">
        <div className="error">{error}</div>
        <button onClick={loadForms} className="retry-btn">重试</button>
      </div>
    );
  }

  if (forms.length === 0) {
    return (
      <div className="form-wheel-container">
        <div className="no-forms">暂无可用表单</div>
        <button onClick={loadForms} className="retry-btn">刷新</button>
      </div>
    );
  }

  return (
    <div className="form-wheel-container">
      <div className="form-wheel-header">
        <h2>表单转盘</h2>
        <p>点击转盘，随机选择一个表单来填写</p>
      </div>

      <div className="wheel-container">
        <div 
          ref={wheelRef}
          className="form-wheel"
          style={{ transform: `rotate(${rotation}deg)` }}
        >
          {forms.map((form, index) => {
            const angle = getItemAngle(index, forms.length);
            const itemStyle = {
              transform: `rotate(${angle}deg)`,
              '--item-bg': `hsl(${(index * 360 / forms.length)}, 70%, 85%)`
            };
            
            return (
              <div key={form.id} className="wheel-item" style={itemStyle}>
                <div className="wheel-item-content">
                  <div className="form-title">{form.title || '未命名表单'}</div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* 转盘中心按钮 */}
        <div className="wheel-center">
          <button 
            className={`spin-button ${isSpinning ? 'spinning' : ''}`}
            onClick={startSpin}
            disabled={isSpinning}
          >
            {isSpinning ? '转盘中...' : '开始转盘'}
          </button>
        </div>
        
        {/* 指针 */}
        <div className="wheel-pointer"></div>
      </div>

      {/* 结果显示 */}
      {selectedForm && (
        <div className="result-container">
          <div className="result-card">
            <h3>🎉 恭喜！抽中了表单：</h3>
            <div className="selected-form">
              <h4>{selectedForm.title || '未命名表单'}</h4>
              {selectedForm.description && (
                <p className="form-description">{selectedForm.description}</p>
              )}
              <div className="form-info">
                <span>创建时间: {new Date(selectedForm.createdAt).toLocaleDateString('zh-CN')}</span>
                {selectedForm.submissionCount !== undefined && (
                  <span>已提交: {selectedForm.submissionCount} 次</span>
                )}
              </div>
            </div>
            <div className="result-actions">
              <button 
                className="open-form-btn"
                onClick={() => openForm(selectedForm)}
              >
                打开表单
              </button>
              <button 
                className="spin-again-btn"
                onClick={startSpin}
              >
                再来一次
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 表单列表 */}
      <div className="forms-list">
        <h3>所有可用表单 ({forms.length})</h3>
        <div className="forms-grid">
          {forms.map(form => (
            <div key={form.id} className="form-card">
              <div className="card-header">
                <h4>{form.title || '未命名表单'}</h4>
                <button 
                  className={`card-wheel-btn ${cardSpinning[form.id] ? 'spinning' : ''}`}
                  onClick={() => spinCardWheel(form.id)}
                  disabled={cardSpinning[form.id]}
                  title="转盘打开"
                >
                  <span className="wheel-icon">🎯</span>
                </button>
              </div>
              {form.description && (
                <p className="form-desc">{form.description}</p>
              )}
              <div className="form-meta">
                <span>创建: {new Date(form.createdAt).toLocaleDateString('zh-CN')}</span>
                {form.submissionCount !== undefined && (
                  <span>提交: {form.submissionCount}</span>
                )}
              </div>
              <div className="card-actions">
                <button 
                  className="direct-open-btn"
                  onClick={() => openForm(form)}
                >
                  直接打开
                </button>
                <button 
                  className={`card-wheel-btn-large ${cardSpinning[form.id] ? 'spinning' : ''}`}
                  onClick={() => spinCardWheel(form.id)}
                  disabled={cardSpinning[form.id]}
                >
                  {cardSpinning[form.id] ? '转盘中...' : '转盘打开'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
